import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { openRazorpay } from '../utils/razorpay';
import { saveSubscription } from '../services/firestoreService';
import { setPlanCache } from '../utils/planCache';
import './PricingPage.css';

// ── Plan definitions ─────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'free',
    name: 'Starter',
    monthlyPrice: 0,
    yearlyPrice: 0,
    yearlyTotal: 0,
    badgeType: null,
    cardClass: '',
    btnClass: 'current-btn',
    features: [
      { text: '10 trades per month',        included: true  },
      { text: 'Basic trade journal',         included: true  },
      { text: 'Simple P&L tracking',         included: true  },
      { text: '1 AI analysis per week',      included: true  },
      { text: 'Advanced reports',            included: false },
      { text: 'Psychology tracker',          included: false },
      { text: 'Risk manager',                included: false },
      { text: 'Priority support',            included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 999,
    yearlyPrice: 799,
    yearlyTotal: 9588,
    badgeType: 'popular',
    cardClass: 'popular',
    btnClass: 'upgrade-btn',
    features: [
      { text: 'Unlimited trades',            included: true  },
      { text: 'Full trade journal',          included: true  },
      { text: 'Advanced P&L reports',        included: true  },
      { text: 'Weekly + Monthly reports',    included: true  },
      { text: 'Unlimited AI analyses',       included: true  },
      { text: 'Full psychology tracker',     included: true  },
      { text: 'Risk manager',                included: true  },
      { text: 'Pattern recognition',         included: true  },
      { text: 'Priority email support',      included: true  },
      { text: '1-on-1 coaching',             included: false },
      { text: 'Custom AI model',             included: false },
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    monthlyPrice: 1999,
    yearlyPrice: 1599,
    yearlyTotal: 19188,
    badgeType: 'elite',
    cardClass: 'elite',
    btnClass: 'elite-btn',
    features: [
      { text: 'Everything in Pro',                    included: true },
      { text: '1-on-1 AI coaching session/month',     included: true },
      { text: 'Custom risk rules & alerts',           included: true },
      { text: 'Advanced pattern recognition',         included: true },
      { text: 'Portfolio analysis',                   included: true },
      { text: 'Broker integration (coming soon)',     included: true },
      { text: 'WhatsApp alerts (coming soon)',        included: true },
      { text: 'Dedicated support',                   included: true },
      { text: 'Early access to new features',        included: true },
      { text: 'Export all data',                     included: true },
    ],
  },
];

const PLAN_ORDER = ['free', 'pro', 'elite'];

// ── Comparison table data ─────────────────────────────────────────────────────
const COMPARISON = [
  { cat: 'TRADING' },
  { label: 'Trades per month',    free: '10',        pro: 'Unlimited', elite: 'Unlimited' },
  { label: 'Trade journal',       free: 'Basic',     pro: 'Full',      elite: 'Full' },
  { label: 'Import trades (CSV)', free: false,       pro: 'soon',      elite: 'soon' },

  { cat: 'ANALYTICS' },
  { label: 'P&L Reports',         free: 'Basic',     pro: 'Advanced',  elite: 'Advanced' },
  { label: 'Weekly comparison',   free: false,       pro: true,        elite: true },
  { label: 'Monthly comparison',  free: false,       pro: true,        elite: true },
  { label: 'Symbol analysis',     free: false,       pro: 'Full',      elite: 'Full' },

  { cat: 'AI FEATURES' },
  { label: 'AI analyses/week',    free: '1',         pro: 'Unlimited', elite: 'Unlimited' },
  { label: 'Psychology tracker',  free: false,       pro: 'Full',      elite: 'Full' },
  { label: 'Pattern recognition', free: false,       pro: true,        elite: true },
  { label: 'AI coaching',         free: false,       pro: false,       elite: 'Monthly session' },

  { cat: 'RISK' },
  { label: 'Risk manager',        free: false,       pro: true,        elite: true },
  { label: 'Custom alerts',       free: false,       pro: 'Basic',     elite: 'Advanced' },
  { label: 'WhatsApp alerts',     free: false,       pro: false,       elite: 'soon' },

  { cat: 'SUPPORT' },
  { label: 'Support',             free: 'Community', pro: 'Priority Email', elite: 'Dedicated' },
];

const FAQ_ITEMS = [
  {
    q: 'Kya main plan change kar sakta hun?',
    a: 'Haan, kisi bhi time upgrade ya downgrade kar sakte ho. Upgrade instant hota hai; downgrade next billing cycle pe apply hota hai.',
  },
  {
    q: 'Payment methods kya hain?',
    a: 'UPI, Credit/Debit Card, Net Banking — sab accept karte hain via Razorpay. Completely secure aur encrypted.',
  },
  {
    q: 'Kya free trial hai?',
    a: 'Free plan permanently free hai — koi credit card required nahi. Pro aur Elite ke liye 7-day free trial milta hai.',
  },
  {
    q: 'Data safe hai?',
    a: 'Haan. Aapka data Firebase (Google Cloud) mein securely store hota hai. Aapka data sirf aapka hai — hum kabhi third party ko share nahi karte.',
  },
  {
    q: 'Refund policy kya hai?',
    a: '7 din mein full refund milega bina kisi sawaal ke. Support: support@tradefixai.in',
  },
];

// ── Cell renderer for comparison table ───────────────────────────────────────
const CellVal = ({ val }) => {
  if (val === true)   return <span className="ct-check">✓</span>;
  if (val === false)  return <span className="ct-cross">✗</span>;
  if (val === 'soon') return <span className="ct-soon">Coming Soon</span>;
  return <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{val}</span>;
};

// ── Main component ────────────────────────────────────────────────────────────
const PricingPage = () => {
  const navigate = useNavigate();
  const [yearly, setYearly]             = useState(false);
  const [userPlan, setUserPlan]         = useState('free');
  const [showTable, setShowTable]       = useState(false);
  const [openFaq, setOpenFaq]           = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const loadPlan = async () => {
      if (!auth.currentUser) return;
      try {
        const snap = await getDoc(doc(db, 'subscriptions', auth.currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          const pid = data.planId || data.plan || 'free';
          setUserPlan(pid);
        }
      } catch (_) {}
    };
    loadPlan();
  }, []);

  const getButtonLabel = (planId) => {
    if (planId === userPlan) return 'Current Plan';
    const curr = PLAN_ORDER.indexOf(userPlan);
    const target = PLAN_ORDER.indexOf(planId);
    if (target > curr) return planId === 'free' ? 'Get Started Free' : `Upgrade to ${PLANS.find(p => p.id === planId)?.name}`;
    return `Switch to ${PLANS.find(p => p.id === planId)?.name}`;
  };

  const getBtnClass = (plan) => {
    if (plan.id === userPlan) return 'plan-btn current-btn';
    if (plan.id === 'elite')  return 'plan-btn elite-btn';
    if (plan.id === 'pro')    return 'plan-btn upgrade-btn';
    if (plan.id === 'basic')  return 'plan-btn outline-btn';
    return 'plan-btn current-btn';
  };

  const handleUpgrade = async (planKey) => {
    const user = auth.currentUser;
    if (!user) { navigate('/login'); return; }

    setPaymentLoading(planKey);

    await openRazorpay(
      planKey,
      user,
      async (paymentResponse, key) => {
        try {
          const priceMap = { pro: 999, elite: 1999 };
          const startDate = new Date();
          const endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + 1);

          await saveSubscription(user.uid, {
            planId: key,
            planName: key.charAt(0).toUpperCase() + key.slice(1),
            price: priceMap[key] || 0,
            status: 'active',
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            razorpayPaymentId: paymentResponse.razorpay_payment_id,
            createdAt: new Date().toISOString(),
          });

          setPlanCache(key);
          setUserPlan(key);
          alert(`🎉 Welcome to ${key.charAt(0).toUpperCase() + key.slice(1)} Plan!`);
          navigate('/dashboard');
        } catch (err) {
          console.error('Plan activation error:', err);
          alert('Payment received but activation failed. Email support@tradefixai.in with your payment ID: ' + paymentResponse.razorpay_payment_id);
        }
      }
    );

    setPaymentLoading(false);
  };

  const price = (plan) => yearly && plan.yearlyPrice > 0 ? plan.yearlyPrice : plan.monthlyPrice;

  return (
    <div className="pricing-page">

      {/* ── Header ── */}
      <div className="pricing-header">
        <h1 className="pricing-title">Simple, Transparent Pricing</h1>
        <p className="pricing-subtitle">Choose the plan that fits your trading journey</p>

        <div className="toggle-wrapper">
          <span
            className={`toggle-label ${!yearly ? 'active' : ''}`}
            onClick={() => setYearly(false)}
          >
            Monthly
          </span>
          <button
            className={`toggle-switch ${yearly ? 'yearly' : ''}`}
            onClick={() => setYearly(y => !y)}
            aria-label="Toggle billing period"
          />
          <span
            className={`toggle-label ${yearly ? 'active' : ''}`}
            onClick={() => setYearly(true)}
          >
            Yearly
          </span>
          {yearly && <span className="save-badge">Save 20%</span>}
        </div>
      </div>

      {/* ── Plan Cards ── */}
      <div className="plans-grid">
        {PLANS.map(plan => {
          const isCurrent = plan.id === userPlan;
          const showBadge = isCurrent ? 'current' : plan.badgeType;
          return (
            <div key={plan.id} className={`plan-card ${plan.cardClass}`}>
              {showBadge && (
                <div className={`plan-badge ${showBadge}`}>
                  {showBadge === 'current' ? 'Current Plan'
                   : showBadge === 'popular' ? 'Most Popular'
                   : 'Best Value'}
                </div>
              )}

              <div className="plan-name">{plan.name}</div>

              {yearly && plan.monthlyPrice > 0 && (
                <div className="plan-original-price">₹{plan.monthlyPrice}/mo</div>
              )}
              <div className="plan-price">
                ₹{price(plan).toLocaleString()}
                <span>/mo</span>
              </div>
              <div className="plan-yearly-note">
                {yearly && plan.yearlyTotal > 0
                  ? `Billed ₹${plan.yearlyTotal.toLocaleString()}/year`
                  : plan.monthlyPrice === 0
                  ? 'Free forever'
                  : 'Billed monthly'}
              </div>

              <hr className="plan-divider" />

              <ul className="feature-list">
                {plan.features.map((f, i) => (
                  <li key={i} className={`feature-item ${f.included ? 'included' : 'locked'}`}>
                    {f.included
                      ? <span className="feature-check">✓</span>
                      : <span className="feature-cross">✗</span>}
                    <span className="feature-text">{f.text}</span>
                  </li>
                ))}
              </ul>

              <button
                className={getBtnClass(plan)}
                onClick={() => plan.id !== 'free' && !isCurrent && handleUpgrade(plan.id)}
                disabled={isCurrent || plan.id === 'free' || paymentLoading === plan.id}
              >
                {paymentLoading === plan.id ? 'Processing...' : getButtonLabel(plan.id)}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Guarantee Banner ── */}
      <div className="guarantee-banner">
        <span style={{ fontSize: 24 }}>🛡️</span>
        <div>
          <div className="guarantee-text">7-day money-back guarantee on all paid plans</div>
          <div className="guarantee-sub">No questions asked · Cancel anytime · support@tradefixai.in</div>
        </div>
      </div>

      {/* ── Affiliate CTA ── */}
      <div className="affiliate-banner">
        <div>
          <div className="affiliate-text">💸 Refer traders and earn 30% commission</div>
          <div className="affiliate-sub">Join our affiliate program — earn for every successful referral</div>
        </div>
        <button className="affiliate-btn" onClick={() => navigate('/affiliate')}>
          Learn More →
        </button>
      </div>

      {/* ── Comparison Table ── */}
      <div className="comparison-section">
        <div className="section-title">Compare All Features</div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <button className="comparison-toggle-btn" onClick={() => setShowTable(t => !t)}>
            {showTable ? 'Hide comparison ↑' : 'Show full comparison ↓'}
          </button>
        </div>

        {showTable && (
          <div className="comparison-table-wrap">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Starter</th>
                  <th className="pro-col">Pro ⭐</th>
                  <th>Elite</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => {
                  if (row.cat) {
                    return (
                      <tr key={i} className="cat-row">
                        <td>{row.cat}</td>
                        <td />
                        <td className="pro-col" />
                        <td />
                      </tr>
                    );
                  }
                  return (
                    <tr key={i}>
                      <td>{row.label}</td>
                      <td><CellVal val={row.free} /></td>
                      <td className="pro-col"><CellVal val={row.pro} /></td>
                      <td><CellVal val={row.elite} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── FAQ ── */}
      <div className="faq-section">
        <div className="faq-title">Frequently Asked Questions</div>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="faq-item">
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{item.q}</span>
                <span className={`faq-chevron ${openFaq === i ? 'open' : ''}`}>▾</span>
              </button>
              {openFaq === i && (
                <div className="faq-answer">{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default PricingPage;
